import { dlog } from '../utils/debug';
import { useState, useEffect } from 'react';
import { ref, onValue, set } from 'firebase/database';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to manage user subscription status and limits
 * @returns {Object} Subscription state and utilities
 */
export default function useSubscription() {
  const { user } = useAuth();
  const [isPro, setIsPro] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      setIsPro(false);
      setSubscriptionData(null);
      setLoading(false);
      setError(null);
      return;
    }

    // Listen to user's subscription status in Firebase
    const subscriptionRef = ref(db, `users/${user.uid}/subscription`);
    
    const unsubscribe = onValue(
      subscriptionRef, 
      (snapshot) => {
        try {
          const data = snapshot.val();
          
          dlog('🔍 Subscription data:', data);
          
          if (data && data.status === 'active') {
            dlog('✅ User is Pro subscriber');
            setIsPro(true);
            setSubscriptionData({
              planType: data.planType,
              status: data.status,
              nextBillingDate: data.nextBillingDate,
              stripeCustomerId: data.stripeCustomerId,
              stripeSubscriptionId: data.stripeSubscriptionId
            });
          } else {
            dlog('❌ User is not a subscriber or no data');
            setIsPro(false);
            setSubscriptionData(null);
          }
          
          setError(null);
          setLoading(false);
        } catch (err) {
          console.error('Error reading subscription data:', err);
          setError(err.message || 'Failed to load subscription status');
          setIsPro(false);
          setSubscriptionData(null);
          setLoading(false);
        }
      },
      (error) => {
        console.error('Firebase subscription listener error:', error);
        setError('Failed to load subscription status. Using default free tier.');
        setIsPro(false);
        setSubscriptionData(null);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  /**
   * Get match limit for free users
   * @returns {Object} Match count and retention period
   */
  const getMatchLimit = () => {
    if (isPro) {
      return { count: Infinity, days: Infinity };
    }
    return { count: 3, days: Infinity }; // Free tier: Last 3 matches only, no time limit
  };

  /**
   * Clear subscription data (for testing/debugging)
   * This will remove any test subscription data
   */
  const clearSubscriptionData = async () => {
    if (!user) return;
    
    try {
      const subscriptionRef = ref(db, `users/${user.uid}/subscription`);
      await set(subscriptionRef, null);
      dlog('🧹 Cleared subscription data');
    } catch (error) {
      console.error('Error clearing subscription data:', error);
    }
  };

  return {
    isPro,
    subscriptionData,
    loading,
    error,
    getMatchLimit,
    clearSubscriptionData
  };
}
